// Eurodrive app routes
// - Eurodrive routes: Regular user-facing views (EurodriveLayout)
// - SDE routes: Admin-only views (SdeLayout)

// SDE views (admin-only)

const Dashboard = () => import("sde-client/views/Dashboard.vue");
const Search = () => import("sde-client/views/Search.vue");
const Space = () => import("sde-client/views/Space.vue");
const Login = () => import("sde-client/views/Login.vue");
const ResetPassword = () =>
  import("sde-client/components/login/ResetPassword.vue");
const SetPassword = () => import("sde-client/components/login/SetPassword.vue");
const NotFound = () => import("sde-client/views/404.vue");
const ExternalSharing = () =>
  import("sde-client/views/ExternalSharing/ExternalSharing.vue");
const Disclaimer = () => import("sde-client/components/login/Disclaimer.vue");

// Admin views
const AdminSettings = () => import("sde-client/views/admin/Settings.vue");
const AdminTableSettings = () =>
  import("sde-client/views/admin/TableSettings.vue");
const AdminUsers = () => import("sde-client/views/admin/Users.vue");
const AdminGroups = () => import("sde-client/views/admin/Groups.vue");
const AdminAudits = () => import("sde-client/views/admin/Audits.vue");
const AdminSpaces = () => import("sde-client/views/admin/Spaces.vue");
const AdminOverview = () => import("sde-client/views/admin/Overview.vue");
const AdminJobs = () => import("sde-client/views/admin/Jobs.vue");
const AdminJobsList = () => import("sde-client/views/admin/AdminJobsList.vue");
const AdminJobProfiles = () =>
  import("sde-client/views/admin/AdminJobProfiles.vue");
const AdminSystemAttributes = () =>
  import("sde-client/views/admin/ServerAttributes.vue");
const AdminSystemInformation = () =>
  import("sde-client/views/admin/SystemInformation.vue");
const AdminPortals = () => import("sde-client/views/admin/Portals.vue");
const Widgets = () => import("sde-client/views/admin/Widgets.vue");
const Templates = () => import("sde-client/views/admin/Templates.vue");

// Eurodrive admin views
const EurodriveSettings = () => import("~/views/admin/EurodriveSettings.vue");

// Profile views (admin-only in Eurodrive)
const ProfileAccount = () =>
  import("sde-client/components/profile/Account.vue");
const ProfilePreferences = () =>
  import("sde-client/components/profile/Preferences.vue");
const ProfileSubscriptions = () =>
  import("sde-client/components/profile/Subscriptions.vue");
const ProfileShared = () => import("sde-client/components/profile/Shared.vue");

// Widgets
const MainWidget = () =>
  import("sde-client/components/shared/Widgets/MainWidget.vue");

// Layouts
const EurodriveLayout = () => import("~/layouts/EurodriveLayout.vue");
const SdeLayout = () => import("~/layouts/SdeLayout.vue");

// Eurodrive-specific views
const EurodriveDashboard = () => import("~/views/EurodriveDashboard.vue");
const EurodriveCars = () => import("~/views/EurodriveCars.vue");
const EurodriveCustomers = () => import("~/views/EurodriveCustomers.vue");
const EurodriveInsuranceCases = () =>
  import("~/views/EurodriveInsuranceCases.vue");
const EurodriveTickets = () => import("~/views/EurodriveTickets.vue");
const EurodriveCalendar = () => import("~/views/EurodriveCalendar.vue");
const EurodriveSearch = () => import("~/views/EurodriveSearch.vue");
const EurodriveComplaints = () => import("~/views/EurodriveComplaints.vue");

// Login
const LoginForm = () => import("sde-client/components/login/LoginForm.vue");

const routes = [
  // Eurodrive routes (regular users)
  {
    path: "/eurodrive",
    component: EurodriveLayout,
    children: [
      {
        path: "",
        name: "home",
        component: EurodriveDashboard,
        meta: { breadCrumb: "dashboard", footer: true },
      },
      {
        path: "cars",
        name: "cars",
        component: EurodriveCars,
        meta: { breadCrumb: "cars overview", footer: true },
      },
      {
        path: "customers",
        name: "customers",
        component: EurodriveCustomers,
        meta: { breadCrumb: "customers overview", footer: true },
      },
      {
        path: "insurance-cases",
        name: "insuranceCases",
        component: EurodriveInsuranceCases,
        meta: { breadCrumb: "insurance cases", footer: true },
      },
      {
        path: "tickets",
        name: "tickets",
        component: EurodriveTickets,
        meta: { breadCrumb: "tickets overview", footer: true },
      },
      {
        path: "calendar",
        name: "calendar",
        component: EurodriveCalendar,
        meta: { breadCrumb: "calendar", footer: false },
      },
      {
        path: "complaints",
        name: "complaints",
        component: EurodriveComplaints,
        meta: { breadCrumb: "complaints", footer: true },
      },
      {
        path: "search",
        name: "eurodriveSearch",
        component: EurodriveSearch,
        meta: { breadCrumb: "vyhledávání", footer: true },
      },
    ],
  },
  // SDE routes (admin-only)
  {
    path: "/",
    component: SdeLayout,
    meta: { adminRequired: true },
    children: [
      {
        path: "",
        name: "sde-dashboard",
        component: Dashboard,
        meta: { breadCrumb: "dashboard", footer: true },
      },
      {
        name: "search",
        path: "search",
        component: Search,
        meta: { breadCrumb: "search", footer: true },
      },
      {
        name: "space",
        path: "spaces/:id",
        component: Space,
        children: [
          { name: "container", path: ":containerId", component: Space },
        ],
        meta: { footer: true },
      },
      {
        path: "profile",
        meta: { footer: true, profileSideBar: true },
        children: [
          {
            path: "account",
            name: "account",
            component: ProfileAccount,
            meta: { breadCrumb: "account" },
          },
          {
            path: "preferences",
            name: "preferences",
            component: ProfilePreferences,
            meta: { breadCrumb: "preferences" },
          },
          {
            path: "subscriptions",
            name: "subscriptions",
            component: ProfileSubscriptions,
            meta: { breadCrumb: "subscriptions" },
          },
          {
            path: "shared",
            name: "shared",
            component: ProfileShared,
            meta: { breadCrumb: "shared" },
          },
        ],
      },
      {
        path: "admin",
        meta: { adminSideBar: true, footer: false },
        children: [
          {
            path: "overview",
            name: "overview",
            component: AdminOverview,
            meta: { breadCrumb: "overview" },
          },
          {
            path: "users",
            name: "users",
            component: AdminUsers,
            meta: { breadCrumb: "users", permissionRequired: "EDIT_USER" },
          },
          {
            path: "groups",
            name: "groups",
            component: AdminGroups,
            meta: { breadCrumb: "groups", permissionRequired: "EDIT_GROUP" },
          },
          {
            path: "spaces",
            name: "spaces",
            component: AdminSpaces,
            meta: { breadCrumb: "spaces", permissionRequired: "EDIT_SPACES" },
          },
          {
            path: "audits",
            name: "audits",
            component: AdminAudits,
            meta: { breadCrumb: "audits", permissionRequired: "VIEW_AUDITS" },
          },
          {
            path: "jobs",
            component: AdminJobs,
            meta: { breadCrumb: "jobs" },
            children: [
              {
                path: "",
                name: "jobs",
                component: AdminJobsList,
                meta: { permissionRequired: "EDIT_JOBS" },
              },
              {
                path: "profiles",
                name: "jobProfiles",
                component: AdminJobProfiles,
                meta: {
                  breadCrumb: "jobProfiles",
                  permissionRequired: "EDIT_JOBPROFILES",
                },
              },
            ],
          },
          {
            path: "settings",
            name: "settings",
            component: AdminSettings,
            meta: {
              breadCrumb: "settings",
              permissionRequired: "EDIT_APP_DATA",
            },
          },
          {
            path: "table-settings",
            name: "tableSettings",
            component: AdminTableSettings,
            meta: {
              breadCrumb: "tableSettings",
              permissionRequired: "EDIT_APP_DATA",
            },
          },
          {
            path: "portals",
            name: "portals",
            component: AdminPortals,
            meta: {
              breadCrumb: "portals",
              permissionRequired: "EDIT_APP_DATA",
            },
          },
          {
            path: "system-attributes",
            name: "systemAttributes",
            component: AdminSystemAttributes,
            meta: {
              breadCrumb: "systemAttributes",
              permissionRequired: "EDIT_APP_DATA",
            },
          },
          {
            path: "widgets",
            name: "widgets",
            component: Widgets,
            meta: { breadCrumb: "widgets" },
          },
          {
            path: 'templates',
            name: 'templates',
            component: Templates,
            meta: {
              breadCrumb: 'templates'
            }
          },
          {
            path: "system-information",
            name: "systemInformation",
            component: AdminSystemInformation,
            meta: {
              breadCrumb: "systemInformation",
              permissionRequired: "SUPER_ADMIN",
            },
          },
          {
            path: "eurodrive",
            name: "eurodriveSettings",
            component: EurodriveSettings,
            meta: {
              breadCrumb: "eurodrive",
              permissionRequired: "EDIT_APP_DATA",
            },
          },
        ],
      },
    ],
  },
  // Auth routes
  {
    path: "/user",
    component: Login,
    meta: { public: true },
    children: [
      {
        path: "login",
        name: "login",
        component: LoginForm,
        props: { homeRoute: "/eurodrive" },
      },
      {
        path: "reset-password",
        name: "resetPassword",
        component: ResetPassword,
      },
      { path: "set-password", name: "setPassword", component: SetPassword },
      { path: "disclaimer", name: "disclaimer", component: Disclaimer },
    ],
  },
  { path: "/.well-known/change-password", redirect: { name: "resetPassword" } },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFound,
    meta: { public: true, breadCrumb: "error404" },
  },
  {
    path: "/share/:id",
    name: "ExternalSharing",
    component: ExternalSharing,
    meta: { public: true },
  },
];

export default routes;
